import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { ArrowLeft, CreditCard, Shield, Check, Loader2, Upload, Building2, QrCode, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../../store/authStore';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CheckoutPage() {
  const navigate = useNavigate();
  const { planId } = useParams();
  const { user, checkAuth } = useAuthStore();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  
  // Payment status states
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'failed', null
  const [showAlternativePayment, setShowAlternativePayment] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  
  // Manual payment states
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [transactionRef, setTransactionRef] = useState('');
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);

  useEffect(() => {
    fetchPlan();
    fetchBankDetails();
  }, [planId]);

  const fetchPlan = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/plans`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const foundPlan = data.find(p => p.plan_id === planId);
        if (foundPlan) {
          setPlan(foundPlan);
          setBillingCycle(foundPlan.billing_cycle || 'monthly');
        } else {
          toast.error('Plan not found');
          navigate('/select-plan');
        }
      } else {
        toast.error('Plan not found');
        navigate('/select-plan');
      }
    } catch (error) {
      toast.error('Failed to load plan');
      navigate('/select-plan');
    } finally {
      setLoading(false);
    }
  };

  const fetchBankDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/platform-settings/bank-details`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setBankDetails(data);
      }
    } catch (error) {
      console.log('Bank details not configured');
    }
  };

  const getPrice = () => {
    if (!plan) return 0;
    if (billingCycle === 'yearly') {
      return plan.price_yearly || plan.price * 10;
    }
    return plan.price_monthly || plan.price;
  };

  const handleCheckout = async () => {
    setProcessing(true);
    setPaymentStatus(null);
    
    try {
      // Create subscription order
      const response = await fetch(`${API_URL}/api/subscriptions/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          plan_id: planId,
          billing_cycle: billingCycle
        })
      });

      if (response.ok) {
        const order = await response.json();
        
        // Check if Razorpay is available
        if (window.Razorpay && order.razorpay_order_id) {
          const options = {
            key: order.razorpay_key_id,
            amount: order.amount,
            currency: order.currency || 'INR',
            name: 'YellowGold Pro',
            description: `${plan.display_name} - ${billingCycle}`,
            order_id: order.razorpay_order_id,
            handler: async function(razorpayResponse) {
              // Verify payment
              try {
                const verifyResponse = await fetch(`${API_URL}/api/subscriptions/verify-payment`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({
                    order_id: order.order_id,
                    razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                    razorpay_order_id: razorpayResponse.razorpay_order_id,
                    razorpay_signature: razorpayResponse.razorpay_signature
                  })
                });

                if (verifyResponse.ok) {
                  setPaymentStatus('success');
                  toast.success('Payment successful! Your subscription is now active.');
                  await checkAuth();
                  // Auto redirect after 3 seconds
                  setTimeout(() => navigate('/dashboard'), 3000);
                } else {
                  setPaymentStatus('failed');
                  toast.error('Payment verification failed. Please contact support.');
                  setShowAlternativePayment(true);
                }
              } catch (error) {
                setPaymentStatus('failed');
                toast.error('Payment verification failed');
                setShowAlternativePayment(true);
              }
            },
            modal: {
              ondismiss: function() {
                setPaymentStatus('failed');
                toast.error('Payment was cancelled');
                setShowAlternativePayment(true);
              }
            },
            prefill: {
              email: user?.email,
              contact: user?.mobile
            },
            theme: {
              color: '#EAB308'
            }
          };

          const razorpay = new window.Razorpay(options);
          razorpay.on('payment.failed', function(response) {
            setPaymentStatus('failed');
            toast.error(`Payment failed: ${response.error.description}`);
            setShowAlternativePayment(true);
          });
          razorpay.open();
        } else {
          // Razorpay not configured
          setPaymentStatus('failed');
          toast.error('Payment gateway is currently unavailable.');
          setShowAlternativePayment(true);
        }
      } else {
        const error = await response.json();
        setPaymentStatus('failed');
        toast.error(error.detail || 'Failed to create order');
        setShowAlternativePayment(true);
      }
    } catch (error) {
      setPaymentStatus('failed');
      toast.error('Checkout failed. Please try again.');
      setShowAlternativePayment(true);
    } finally {
      setProcessing(false);
    }
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setScreenshotFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleManualPaymentSubmit = async () => {
    if (!screenshotFile) {
      toast.error('Please upload payment screenshot');
      return;
    }
    if (!transactionRef.trim()) {
      toast.error('Please enter transaction reference/UTR number');
      return;
    }

    setUploadingScreenshot(true);
    try {
      const formData = new FormData();
      formData.append('screenshot', screenshotFile);
      formData.append('transaction_ref', transactionRef);
      formData.append('plan_id', planId);
      formData.append('billing_cycle', billingCycle);
      formData.append('amount', (getPrice() * 1.18).toFixed(2));

      const response = await fetch(`${API_URL}/api/manual-payments/submit`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        toast.success('Payment screenshot submitted! Waiting for admin approval.');
        setPaymentStatus('pending');
        setTimeout(() => navigate('/dashboard'), 3000);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit payment');
      }
    } catch (error) {
      toast.error('Failed to submit payment screenshot');
    } finally {
      setUploadingScreenshot(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!plan) {
    return null;
  }

  // Payment Success Screen
  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-green-500">Payment Successful!</h2>
            <p className="text-muted-foreground">
              Your subscription to {plan.display_name} is now active.
            </p>
            <p className="text-sm text-muted-foreground">
              Redirecting to dashboard...
            </p>
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Payment Pending Screen (Manual Payment)
  if (paymentStatus === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-500/20 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-amber-500" />
            </div>
            <h2 className="text-2xl font-bold text-amber-500">Payment Pending Approval</h2>
            <p className="text-muted-foreground">
              Your payment screenshot has been submitted. Our team will verify and activate your subscription within 24 hours.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Payment Failed Banner */}
        {paymentStatus === 'failed' && (
          <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-3">
            <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-500">Payment Failed</p>
              <p className="text-sm text-muted-foreground">
                Don't worry! You can try again or use alternative payment methods below.
              </p>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          {/* Order Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Order Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <h3 className="font-semibold text-lg">{plan.display_name}</h3>
                <p className="text-sm text-muted-foreground">Professional subscription plan</p>
              </div>

              {/* Billing Cycle Toggle */}
              <div>
                <Label className="text-sm">Billing Cycle</Label>
                <div className="flex gap-2 mt-2">
                  <Button
                    variant={billingCycle === 'monthly' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setBillingCycle('monthly')}
                  >
                    Monthly
                  </Button>
                  <Button
                    variant={billingCycle === 'yearly' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setBillingCycle('yearly')}
                  >
                    Yearly (Save 16%)
                  </Button>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-2 pt-4 border-t">
                <h4 className="font-medium text-sm">What's included:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-1">
                    <Check className="w-3 h-3 text-green-500" />
                    {plan.max_users === -1 ? 'Unlimited' : plan.max_users} Users
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="w-3 h-3 text-green-500" />
                    {plan.max_clients === -1 ? 'Unlimited' : plan.max_clients} Clients
                  </div>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="pt-4 border-t space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>₹{getPrice()}</span>
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>GST (18%)</span>
                  <span>₹{(getPrice() * 0.18).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span className="text-primary">₹{(getPrice() * 1.18).toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Section */}
          <div className="space-y-4">
            {/* Online Payment */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-500" />
                  Secure Payment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                  <p className="text-sm text-green-600 dark:text-green-400">
                    🔒 Your payment is secured by 256-bit SSL encryption
                  </p>
                </div>

                <Button 
                  className="w-full h-12 text-lg"
                  onClick={handleCheckout}
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Pay ₹{(getPrice() * 1.18).toFixed(2)} with Razorpay
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Alternative Payment - Show if payment failed or user clicks */}
            {(showAlternativePayment || paymentStatus === 'failed') && (
              <Card className="border-amber-500/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-500">
                    <Building2 className="w-5 h-5" />
                    Alternative Payment Method
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Pay via bank transfer or UPI and upload the screenshot for verification.
                  </p>

                  {/* Bank Details */}
                  {bankDetails ? (
                    <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                      <h4 className="font-medium">Bank Details:</h4>
                      <div className="text-sm space-y-1">
                        <p><span className="text-muted-foreground">Bank:</span> {bankDetails.bank_name}</p>
                        <p><span className="text-muted-foreground">Account Name:</span> {bankDetails.account_name}</p>
                        <p><span className="text-muted-foreground">Account No:</span> {bankDetails.account_number}</p>
                        <p><span className="text-muted-foreground">IFSC:</span> {bankDetails.ifsc_code}</p>
                        {bankDetails.upi_id && (
                          <p><span className="text-muted-foreground">UPI ID:</span> {bankDetails.upi_id}</p>
                        )}
                      </div>
                      {bankDetails.qr_code_url && (
                        <div className="mt-4">
                          <p className="text-sm font-medium mb-2">Scan QR Code:</p>
                          <img 
                            src={bankDetails.qr_code_url} 
                            alt="Payment QR Code" 
                            className="w-48 h-48 rounded-lg border"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                      Bank details not configured. Please contact support.
                    </div>
                  )}

                  {/* Upload Screenshot */}
                  <div className="space-y-3 pt-4 border-t">
                    <div>
                      <Label>Transaction Reference / UTR Number</Label>
                      <Input 
                        value={transactionRef}
                        onChange={(e) => setTransactionRef(e.target.value)}
                        placeholder="Enter UTR or transaction reference"
                      />
                    </div>

                    <div>
                      <Label>Upload Payment Screenshot</Label>
                      <div className="mt-2">
                        {screenshotPreview ? (
                          <div className="relative">
                            <img 
                              src={screenshotPreview} 
                              alt="Screenshot preview" 
                              className="w-full max-h-48 object-contain rounded-lg border"
                            />
                            <Button
                              variant="destructive"
                              size="sm"
                              className="absolute top-2 right-2"
                              onClick={() => {
                                setScreenshotFile(null);
                                setScreenshotPreview(null);
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                            <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                            <span className="text-sm text-muted-foreground">Click to upload screenshot</span>
                            <span className="text-xs text-muted-foreground">PNG, JPG up to 5MB</span>
                            <input 
                              type="file" 
                              className="hidden" 
                              accept="image/*"
                              onChange={handleScreenshotChange}
                            />
                          </label>
                        )}
                      </div>
                    </div>

                    <Button 
                      className="w-full"
                      onClick={handleManualPaymentSubmit}
                      disabled={uploadingScreenshot || !screenshotFile || !transactionRef.trim()}
                    >
                      {uploadingScreenshot ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Submit for Verification
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Show Alternative Payment Button if not already shown */}
            {!showAlternativePayment && paymentStatus !== 'failed' && (
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setShowAlternativePayment(true)}
              >
                <Building2 className="w-4 h-4 mr-2" />
                Pay via Bank Transfer / UPI
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;
